import PlatformRedirectService from "@/services/PlatformRedirectService";
import { useQuery } from "@tanstack/react-query";

export const PLATFORM_REDIRECT_QUERY_KEY = "platform-redirect";

/**
 * Asks the backend whether the current user must be handed off to lazo-platform (the legacy-UI
 * cutover). The decision is authoritative server-side (feature flag + tenant allowlist + the
 * user's memberships); this hook only transports it.
 *
 * `sourcePath` travels so the hand-off lands on the thing the customer clicked. It is part of the
 * query key: two different old links are two different destinations, and sharing one cache entry
 * would hand the second visitor the first one's link — a one-time code that is already spent.
 *
 * `enabled` gates the call on the caller's cheap pre-check (internal users never ask).
 * `staleTime: Infinity` + `retry: false` because an affirmative answer carries a single-use
 * one-time code: refetching in the background would burn codes, and React Query's dedup already
 * absorbs StrictMode's double-mount. A fresh code is only minted on an explicit `refetch()`.
 */
export function usePlatformHandoff(enabled: boolean, sourcePath?: string) {
  return useQuery({
    queryKey: [PLATFORM_REDIRECT_QUERY_KEY, "handoff", sourcePath ?? null],
    queryFn: () => PlatformRedirectService.handoff(sourcePath).then((r) => r.getContentOrThrowError()),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}
